// Phase 64 — Onboarding reminder banner.
// Renders at the top of Calendar / Jobs whenever the tenant's onboarding
// status is "incomplete". Subscribes to a single fetch on mount; we don't
// poll because the banner only flips when the manager completes/skips,
// both of which navigate AWAY from this layout and re-mount on return.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Sparkles, ArrowRight, X } from "lucide-react";

const DISMISS_KEY = "jb_onboarding_banner_dismissed_until";

export default function OnboardingBanner() {
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Respect a short-lived "remind me later" dismissal so the banner
    // doesn't shout at the manager every single page navigation.
    try {
      const until = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
      if (until && Date.now() < until) {
        setDismissed(true);
        return;
      }
    } catch (_e) { /* ignore */ }
    api.get("/onboarding/status")
      .then(({ data }) => setStatus(data))
      .catch(() => setStatus(null));
  }, []);

  if (dismissed) return null;
  if (!status) return null;
  if (status.completed && (status.missing_critical?.length || 0) === 0) return null;

  // We render once `completed=true` only when there are critical fields
  // still missing (no VAT / no IBAN / no staff). Otherwise the banner
  // would nag managers who deliberately skipped.
  if (status.completed && status.missing_critical.length === 0) return null;

  const missingCount = status.missing_critical?.length || 0;
  const dismiss = () => {
    // Snooze for 24 hours.
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now() + 24 * 60 * 60 * 1000));
    } catch (_e) { /* ignore */ }
    setDismissed(true);
  };

  return (
    <div
      className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center justify-between gap-3 flex-wrap"
      data-testid="onboarding-banner"
    >
      <div className="flex items-center gap-2 text-sm text-amber-900">
        <Sparkles className="h-4 w-4 text-amber-700 shrink-0" />
        <span data-testid="onboarding-banner-text">
          {status.completed ? (
            <>Your setup is mostly done — <strong>{missingCount}</strong> {missingCount === 1 ? "field" : "fields"} still missing ({status.missing_critical.join(", ")}).</>
          ) : (
            <>Welcome! Finish setting up your tenant — <strong>{status.percent}%</strong> done{missingCount > 0 ? `, ${missingCount} ${missingCount === 1 ? "thing" : "things"} still missing` : ""}.</>
          )}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <Link
          to="/onboarding"
          className="text-xs font-bold text-amber-900 underline hover:no-underline flex items-center gap-1"
          data-testid="onboarding-banner-cta"
        >
          Continue setup <ArrowRight className="h-3 w-3" />
        </Link>
        <button
          type="button"
          onClick={dismiss}
          className="text-amber-700 hover:text-amber-900 p-1"
          aria-label="Dismiss for 24 hours"
          data-testid="onboarding-banner-dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
